import React, { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { resolveBackendUrl } from '../services/api';

const menuItems = [
  {
    section: 'Main',
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: '📊' },
    ],
  },
  {
    // No `roles` filter — visible to everyone; backend enforces per-row visibility.
    section: 'Paper Workflow',
    items: [
      { path: '/qp-generation', label: 'QP Generation', icon: '📝' },
    ],
  },
  {
    section: 'Management',
    roles: ['InstituteAdmin'],
    items: [
      { path: '/user-management', label: 'User Management', icon: '👥' },
      { path: '/academic-session', label: 'Academic Session', icon: '🎓' },
      { path: '/course-management', label: 'Course Management', icon: '📚' },
      { path: '/bos-management', label: 'BOS Management', icon: '🏛' },
      { path: '/qp-settings', label: 'QP Settings', icon: '⚙' },
      { path: '/question-bank', label: 'Question Bank', icon: '❓' },
      { path: '/qp-template-library', label: 'QP Template Library', icon: '🗂' },
      { path: '/paper-user-allocations', label: 'Paper User Allocations', icon: '📝' },
    ],
  },
  {
    section: 'Administration',
    roles: ['SuperAdmin'],
    items: [
      { path: '/tenants', label: 'Tenant Management', icon: '🏢' },
      { path: '/settings', label: 'Settings', icon: '⚙' },
    ],
  },
];

function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const getInitials = (name) => {
    return name
      ? name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
      : '??';
  };

  const getPageTitle = () => {
    for (const section of menuItems) {
      for (const item of section.items) {
        if (location.pathname === item.path) return item.label;
      }
    }
    return 'Dashboard';
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className={`app-layout ${collapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Sidebar */}
      <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
        <div
          className="sidebar-header"
          onClick={() => navigate('/dashboard')}
          style={{
            cursor: 'pointer',
            overflow: 'hidden',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: 10,
            width: '100%',
          }}
        >
          {user?.logoUrl && (
            <img
              src={resolveBackendUrl(user.logoUrl) || user.logoUrl}
              alt=""
              className="sidebar-logo-img"
              style={{
                maxHeight: 42,
                maxWidth: collapsed ? 36 : 44,
                objectFit: 'contain',
                flexShrink: 0,
              }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          {!collapsed && (
            <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: 18,
                  letterSpacing: 1,
                  lineHeight: 1.15,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {user?.logoAcronym || 'QPOSM'}
              </h2>
              {user?.tenantName && (
                <p
                  style={{
                    margin: 0,
                    fontSize: 10,
                    opacity: 0.7,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    letterSpacing: 0.5,
                    textTransform: 'none',
                  }}
                  title={user.tenantName}
                >
                  {user.tenantName}
                </p>
              )}
            </div>
          )}
          {collapsed && !user?.logoUrl && (
            <h2>{user?.logoAcronym ? user.logoAcronym.charAt(0) : 'Q'}</h2>
          )}
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((section) => {
            if (section.roles && !section.roles.includes(user?.role)) {
              return null;
            }
            return (
              <div key={section.section}>
                {!collapsed && <div className="nav-section-title">{section.section}</div>}
                {section.items.map((item) => (
                  <button
                    key={item.path}
                    className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                    onClick={() => navigate(item.path)}
                    title={collapsed ? item.label : undefined}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    {!collapsed && <span className="nav-label">{item.label}</span>}
                  </button>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          {!collapsed && (
            <div className="user-info">
              <div className="user-avatar">{getInitials(user?.fullName)}</div>
              <div className="user-details">
                <div className="user-name">{user?.fullName}</div>
                <div className="user-role">{user?.role}</div>
              </div>
            </div>
          )}
          {collapsed && (
            <div className="user-info" style={{ justifyContent: 'center' }}>
              <div className="user-avatar" title={user?.fullName}>{getInitials(user?.fullName)}</div>
            </div>
          )}
          <button className="btn-logout" onClick={handleLogout} title={collapsed ? 'Sign Out' : undefined}>
            <span>⏻</span>
            {!collapsed && ' Sign Out'}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        <header className="top-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="sidebar-toggle" onClick={() => setCollapsed((c) => !c)} title={collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}>
              {collapsed ? '☰' : '◀'}
            </button>
            <h1 className="page-title">{getPageTitle()}</h1>
          </div>
        </header>
        <div className="content-area">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

export default Layout;
